"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useConvexAuth, useMutation, useAction } from "convex/react";
import { useAuth } from "@workos-inc/authkit-react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { invalidateCachedQuery } from "@/hooks/use-cached-query";
import {
    getOfflineReportOwnerId,
    getQueuedReports,
    isOfflineOrNetworkError,
    isRateLimitError,
    markQueuedReportAttempt,
    rateLimitRetryDelayMs,
    offlineStoredImageToBlob,
    removeQueuedReport,
    subscribeToOfflineReportQueue,
    type OfflineQueuedReport,
} from "@/lib/offline-report-queue";

const MAX_RETRY_DELAY_MS = 5 * 60 * 1000;
const BASE_RETRY_DELAY_MS = 30 * 1000;

function retryDelayFor(report: OfflineQueuedReport): number {
    return Math.min(MAX_RETRY_DELAY_MS, Math.max(BASE_RETRY_DELAY_MS, report.attempts * BASE_RETRY_DELAY_MS));
}

function nextEligibleAt(report: OfflineQueuedReport): number | null {
    if (typeof report.nextAttemptAt === "number") return report.nextAttemptAt;
    if (!report.lastAttemptAt) return null;
    return report.lastAttemptAt + retryDelayFor(report);
}

function isDueForRetry(report: OfflineQueuedReport, now: number): boolean {
    const eligibleAt = nextEligibleAt(report);
    if (eligibleAt !== null && now < eligibleAt) return false;
    if (!report.lastAttemptAt) return true;
    return now - report.lastAttemptAt >= retryDelayFor(report);
}

export function useOfflineReportQueue(): OfflineQueuedReport[] {
    const { user } = useAuth();
    const ownerUserId = getOfflineReportOwnerId(user);
    const [reports, setReports] = useState<OfflineQueuedReport[]>([]);

    useEffect(() => {
        let cancelled = false;
        const update = () => {
            void getQueuedReports(ownerUserId).then((queued) => {
                if (!cancelled) setReports(queued);
            });
        };
        update();
        const unsubscribe = subscribeToOfflineReportQueue(update);
        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, [ownerUserId]);

    return reports;
}

export function useOfflineReportSync(): void {
    const { getAccessToken, user } = useAuth();
    const ownerUserId = getOfflineReportOwnerId(user);
    const { isAuthenticated } = useConvexAuth();
    const generateUploadUrl = useMutation(api.aurora.generateUploadUrl);
    const submitAuroraReport = useAction(api.auroraActions.submitAuroraReport);
    const submitCloudReport = useMutation(api.aurora.submitCloudReport);
    const isSyncingRef = useRef(false);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const syncQueuedReportsRef = useRef<() => Promise<void>>(async () => undefined);

    const scheduleNextSync = useCallback((dueAt: number) => {
        if (retryTimerRef.current !== null) {
            clearTimeout(retryTimerRef.current);
        }
        const delay = Math.min(Math.max(dueAt - Date.now() + 1000, 1000), 60 * 60 * 1000);
        retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            void syncQueuedReportsRef.current();
        }, delay);
    }, []);

    const syncQueuedReports = useCallback(async () => {
        if (!isAuthenticated || !ownerUserId || isSyncingRef.current) return;
        if (typeof navigator !== "undefined" && navigator.onLine === false) return;

        const queuedReports = await getQueuedReports(ownerUserId);
        if (queuedReports.length === 0) return;

        isSyncingRef.current = true;
        let soonestWait: number | null = null;
        try {
            const now = Date.now();
            for (const report of queuedReports) {
                if (!isDueForRetry(report, now)) {
                    const eligibleAt = nextEligibleAt(report);
                    if (eligibleAt !== null && eligibleAt > now) {
                        soonestWait = soonestWait === null ? eligibleAt : Math.min(soonestWait, eligibleAt);
                    }
                    continue;
                }

                try {
                    await getAccessToken();

                    if (report.type === "cloud") {
                        await submitCloudReport(report.payload);
                    } else {
                        let imageStorageId: Id<"_storage"> | undefined;
                        if (report.image) {
                            const postUrl = await generateUploadUrl();
                            const blob = await offlineStoredImageToBlob(report.image);
                            const uploadResult = await fetch(postUrl, {
                                method: "POST",
                                headers: { "Content-Type": report.image.type || blob.type || "application/octet-stream" },
                                body: blob,
                            });

                            if (!uploadResult.ok) {
                                throw new Error("Image upload failed");
                            }

                            const uploadJson: unknown = await uploadResult.json();
                            if (
                                typeof uploadJson !== "object" ||
                                uploadJson === null ||
                                !("storageId" in uploadJson) ||
                                typeof uploadJson.storageId !== "string"
                            ) {
                                throw new Error("Image upload failed");
                            }
                            imageStorageId = uploadJson.storageId as Id<"_storage">;
                        }

                        await submitAuroraReport({
                            ...report.payload,
                            imageStorageId,
                        });
                    }

                    await removeQueuedReport(report.id);
                    if (report.type === "cloud") {
                        void invalidateCachedQuery("cloudReports", "cloud-reports-map");
                    } else {
                        void invalidateCachedQuery("auroraReports", "aurora-reports-all");
                    }
                } catch (error) {
                    const retryMs = rateLimitRetryDelayMs(error);
                    const nextAttemptAt = retryMs !== null ? Date.now() + retryMs : undefined;
                    await markQueuedReportAttempt(report.id, error, nextAttemptAt);
                    if (nextAttemptAt !== undefined) {
                        soonestWait = soonestWait === null ? nextAttemptAt : Math.min(soonestWait, nextAttemptAt);
                    }
                    if (isOfflineOrNetworkError(error) || isRateLimitError(error)) break;
                }
            }
        } finally {
            isSyncingRef.current = false;
            if (soonestWait !== null) {
                scheduleNextSync(soonestWait);
            }
        }
    }, [generateUploadUrl, getAccessToken, isAuthenticated, ownerUserId, scheduleNextSync, submitAuroraReport, submitCloudReport]);

    useEffect(() => {
        syncQueuedReportsRef.current = syncQueuedReports;
    }, [syncQueuedReports]);

    useEffect(() => {
        void syncQueuedReports();
    }, [syncQueuedReports]);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const handleSyncTrigger = () => {
            void syncQueuedReports();
        };

        window.addEventListener("online", handleSyncTrigger);
        const unsubscribe = subscribeToOfflineReportQueue(handleSyncTrigger);
        return () => {
            window.removeEventListener("online", handleSyncTrigger);
            unsubscribe();
            if (retryTimerRef.current !== null) {
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }
        };
    }, [syncQueuedReports]);
}

